import { useRouter } from 'next/router';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteE } from 'src/constants/pages.constants';
import { FAQ_ITEMS } from 'src/features/common/faq/containers/constants';
import { ILocale } from 'src/features/common/faq/containers/highlighted-questions/types';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { LandingSection } from 'src/ui/common/layouts/landing-section';
import { Accordion } from 'src/ui/common/molecules/accordion';
import { StyledList, StyledListStep, StyledParagraph } from '../faq-container.styles';

export const HighlightedQuestions: React.VFC<{ title?: string }> = ({ title }) => {
    const { t } = useTranslation(['pages']);
    const { projectName } = useUniverse();
    const router = useRouter();
    const { scrollTo } = router.query;
    const isQueryEmpty = Object.keys(router.query).length === 0;

    const locale: ILocale = useMemo(
        () => ({
            ...t('faq', { returnObjects: true, faqCompanyName: projectName }),
        }),
        [t, projectName]
    );

    useEffect(() => {
        if (!isQueryEmpty && scrollTo === 'faq') {
            document.querySelector('[data-component-id="faq"]')?.scrollIntoView();
        }
    }, []);

    return (
        <div data-component-id="faq">
            <LandingSection seeMoreLink={RouteE.FAQ} title={title ?? locale.landingTitle}>
                <Accordion>
                    <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                        <Typography variant="text" sizeSm="base" color="white">
                            {locale?.whatIsCompany?.title}
                        </Typography>
                    </Accordion.ItemHeader>
                    <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                        <StyledParagraph>
                            {projectName} only offers selected elite NFTs created by well-known profiles and brands.
                            This way you can make sure your NFT has a value beyond the normal. In a world where the
                            process of selecting the right NFT is pure chaos and completely unmanageable, {projectName}{' '}
                            has segmented, selected and offers only the best for you.
                        </StyledParagraph>
                    </Accordion.ItemContent>
                    <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_START}>
                        <Typography variant="text" sizeSm="base" color="white">
                            {locale?.howToStart?.title}
                        </Typography>
                    </Accordion.ItemHeader>
                    <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_START}>
                        <StyledListStep>
                            <li>Register on the website - NFTSuperLab.io.</li>
                            <li>Set up your CryptoX wallet.</li>
                            <li>Connect your CryptoX wallet to your account on NFTSuperLab.</li>
                            <li>You can now start buying NFTs on the website.</li>
                        </StyledListStep>
                    </Accordion.ItemContent>
                    <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                        <Typography variant="text" sizeSm="base" color="white">
                            {locale?.whatIsNFT?.title}
                        </Typography>
                    </Accordion.ItemHeader>
                    <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                        <Spacing pr="20px">
                            {locale?.whatIsNFT?.paragraphs?.map((item) => (
                                <StyledParagraph key={item}>{item}</StyledParagraph>
                            ))}
                            <StyledList>
                                {locale?.whatIsNFT?.fungibleItems?.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </StyledList>
                            <StyledParagraph>{locale?.whatIsNFT?.nonFungible}</StyledParagraph>
                            <StyledList>
                                {locale?.whatIsNFT?.nonFungibleItems?.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </StyledList>
                            <StyledParagraph>{locale?.whatIsNFT?.publiclyOwnership}</StyledParagraph>
                            <StyledList>
                                {locale?.whatIsNFT?.publiclyOwnershipItems?.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </StyledList>
                        </Spacing>
                    </Accordion.ItemContent>
                    <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                        <Typography variant="text" sizeSm="base" color="white">
                            {locale?.whatIsMinting?.title}
                        </Typography>
                    </Accordion.ItemHeader>
                    <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                        {locale?.whatIsMinting?.paragraphs?.map((item) => (
                            <StyledParagraph key={item}>{item}</StyledParagraph>
                        ))}
                    </Accordion.ItemContent>
                </Accordion>
            </LandingSection>
        </div>
    );
};
